/**
 * PreviewTab Component
 * Tab za preview i pokretanje importa
 */

import {
  Grid,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Alert,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  Save as SaveIcon,
  PlayArrow as ImportIcon,
  Storage as DatabaseIcon,
} from '@mui/icons-material';
import { ParsedFeed, FieldTransformOptions } from '../../../lib/xmlParser';
import { CustomField, ImportProgress, ImportResults, RowFilter } from '../types';
import { applyRowFilters } from '../utils/importUtils';
import { CORE_FIELDS, EXTRA_FIELDS } from '../utils/fieldConfig';
import {
  getNestedValue,
  extractTextValue,
  formatKeyValuePreview,
} from '../utils/helpers';

interface PreviewTabProps {
  parsedFeed: ParsedFeed;
  feedName: string;
  mappings: Record<string, string>;
  fieldOptions: Record<string, FieldTransformOptions>;
  customFields: CustomField[];
  importing: boolean;
  importProgress: ImportProgress;
  importResults: ImportResults | null;
  rowFilters?: RowFilter[];
  onSave: () => void;
  onImport: () => void;
  saveDisabled: boolean;
}

export function PreviewTab({
  parsedFeed,
  feedName,
  mappings,
  fieldOptions,
  customFields,
  rowFilters,
  importing,
  importProgress,
  importResults,
  onSave,
  onImport,
  saveDisabled,
}: PreviewTabProps) {
  // Apply row filters to get the actual import count
  const filteredRecords = rowFilters && rowFilters.length > 0
    ? applyRowFilters(parsedFeed.sampleRecords, rowFilters)
    : parsedFeed.sampleRecords;
  const importCount = filteredRecords.length;

  // Get mapped fields (only those with actual mappings)
  const mappedFields = Object.entries(mappings).filter(([_, v]) => v);

  // Get label for a field
  const getFieldLabel = (dbField: string): string => {
    const coreField = CORE_FIELDS.find(f => f.key === dbField);
    if (coreField) return coreField.label;

    const extraField = EXTRA_FIELDS.find(f => f.key === dbField);
    if (extraField) return extraField.label;

    const customField = customFields.find(f => f.key === dbField);
    if (customField) return customField.label;

    return dbField;
  };

  // Get preview value for a record
  const getPreviewValue = (
    record: any,
    dbField: string,
    xmlPath: string
  ): string => {
    const cleanPath = xmlPath.startsWith(parsedFeed.recordElement + '/')
      ? xmlPath.substring(parsedFeed.recordElement.length + 1)
      : xmlPath;

    const options = fieldOptions[dbField];
    const isKeyValue = options?.mode === 'keyValue' && options?.keyField && options?.valueField;

    if (isKeyValue) {
      const rawValue = getNestedValue(record, cleanPath);
      if (Array.isArray(rawValue)) {
        return formatKeyValuePreview(rawValue, options.keyField!, options.valueField!);
      }
    }

    const value = getNestedValue(record, cleanPath);
    return extractTextValue(value) || '-';
  };

  // Static custom fields (those with fixed values)
  const staticCustomFields = customFields.filter(f => f.staticValue);

  return (
    <Grid container spacing={2}>
      {/* Auto Fields Info */}
      <Grid item xs={12}>
        <Alert severity="info" icon={<DatabaseIcon />}>
          <strong>Automatska polja:</strong> Dobavljač = "{feedName || 'unknown'}"
          {staticCustomFields.map(f => (
            <span key={f.id}>, {f.label} = "{f.staticValue}"</span>
          ))}
          <br />
          <small>
            Mapirana polja ({mappedFields.length} kolona):{' '}
            {mappedFields.map(([k]) => k).join(', ') || 'nema'}
          </small>
          <br />
          <small>
            KeyValue polja:{' '}
            {Object.entries(fieldOptions)
              .filter(([_, opt]) => opt?.mode === 'keyValue')
              .map(([k]) => k)
              .join(', ') || 'nema'}
          </small>
        </Alert>
      </Grid>

      {/* Sample Preview Table */}
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          Preview (prvih 5 zapisa)
        </Typography>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                {/* Auto Dobavljač column */}
                <TableCell sx={{ bgcolor: 'info.dark' }}>
                  Dobavljač (auto)
                </TableCell>

                {/* Static custom fields */}
                {staticCustomFields.map(f => (
                  <TableCell key={f.id} sx={{ bgcolor: 'secondary.dark' }}>
                    {f.label} (fiksno)
                  </TableCell>
                ))}

                {/* Mapped fields */}
                {mappedFields.map(([dbField]) => (
                  <TableCell key={dbField}>
                    {getFieldLabel(dbField)}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.slice(0, 5).map((record, idx) => (
                <TableRow key={idx}>
                  {/* Auto Dobavljač value */}
                  <TableCell sx={{ bgcolor: 'info.dark' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {feedName || 'unknown'}
                    </Typography>
                  </TableCell>

                  {/* Static values */}
                  {staticCustomFields.map(f => (
                    <TableCell key={f.id} sx={{ bgcolor: 'secondary.dark' }}>
                      <Typography variant="body2" fontWeight="bold">
                        {f.staticValue}
                      </Typography>
                    </TableCell>
                  ))}

                  {/* Mapped values */}
                  {mappedFields.map(([dbField, xmlPath]) => {
                    const options = fieldOptions[dbField];
                    const isKeyValue = options?.mode === 'keyValue';
                    const displayValue = getPreviewValue(record, dbField, xmlPath);

                    return (
                      <TableCell key={dbField}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            maxWidth: 150,
                            color: isKeyValue ? 'success.main' : 'inherit',
                            fontWeight: isKeyValue ? 600 : 400,
                          }}
                        >
                          {displayValue}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>

      {/* Progress */}
      {importing && (
        <Grid item xs={12}>
          <LinearProgress
            variant="determinate"
            value={importProgress.total > 0
              ? (importProgress.current / importProgress.total) * 100
              : 0
            }
          />
          <Typography variant="body2" sx={{ mt: 1 }}>
            {importProgress.current} / {importProgress.total}
          </Typography>
        </Grid>
      )}

      {/* Results */}
      {importResults && (
        <Grid item xs={12}>
          <Alert severity={importResults.errors > 0 ? 'error' : 'success'}>
            <Typography variant="subtitle1" fontWeight="bold">
              Import završen: {importResults.created} kreirano,{' '}
              {importResults.updated} ažurirano, {importResults.errors} grešaka
            </Typography>
            {importResults.errorDetails && importResults.errorDetails.length > 0 && (
              <Box sx={{ mt: 2, maxHeight: 300, overflow: 'auto' }}>
                <Typography variant="subtitle2" gutterBottom>
                  Detalji grešaka:
                </Typography>
                <Paper variant="outlined" sx={{ p: 1, bgcolor: 'grey.900' }}>
                  {importResults.errorDetails.map((err: string, idx: number) => (
                    <Typography
                      key={idx}
                      variant="body2"
                      component="pre"
                      sx={{
                        m: 0,
                        fontSize: '0.75rem',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-all',
                      }}
                    >
                      {err}
                    </Typography>
                  ))}
                </Paper>
              </Box>
            )}
          </Alert>
        </Grid>
      )}

      {/* Actions */}
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={saveDisabled}
          >
            Spremi profil
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={importing ? <CircularProgress size={20} /> : <ImportIcon />}
            onClick={onImport}
            disabled={importing || !mappings['sku']}
          >
            {importing
              ? 'Importujem...'
              : `Importuj ${importCount} proizvoda`
            }
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
}
